'use client';
import React, { useState } from 'react';
import { Search, MapPin, ArrowRight, Shield, ChevronRight, Building2, Star, Phone } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface Business {
  id: string;
  name: string;
  category: string;
  location: string;
  rating: number;
  reviews: number;
  phone: string;
  verified: boolean;
  description: string;
  hours: string;
}

const BUSINESSES: Business[] = [
  { id: '1', name: 'Nairobi Hardware & Building Supplies', category: 'Hardware', location: 'Industrial Area, Nairobi', rating: 4.6, reviews: 234, phone: '+254 700 123 456', verified: true, description: 'Full range of building materials, tools, and hardware. Wholesale and retail.', hours: 'Mon–Sat 7am–6pm' },
  { id: '2', name: 'Mama Pima Tailoring & Fashion', category: 'Fashion', location: 'Gikomba, Nairobi', rating: 4.8, reviews: 567, phone: '+254 722 234 567', verified: true, description: 'Custom tailoring, alterations, and African wear. 20 years experience.', hours: 'Mon–Sun 8am–7pm' },
  { id: '3', name: 'Coast Seafood Restaurant', category: 'Restaurant', location: 'Nyali, Mombasa', rating: 4.9, reviews: 890, phone: '+254 711 345 678', verified: true, description: 'Fresh seafood, Swahili cuisine, and coastal dishes. Dine-in and takeaway.', hours: 'Daily 11am–10pm' },
  { id: '4', name: 'Kisumu Electronics & Repairs', category: 'Electronics', location: 'Kisumu CBD', rating: 4.4, reviews: 123, phone: '+254 733 456 789', verified: false, description: 'Phone repairs, laptop servicing, and electronics sales.', hours: 'Mon–Sat 8am–6pm' },
  { id: '5', name: 'Green Valley Supermarket', category: 'Supermarket', location: 'Westlands, Nairobi', rating: 4.5, reviews: 445, phone: '+254 700 567 890', verified: true, description: 'Fresh produce, groceries, and household items. Free delivery above KES 2,000.', hours: 'Daily 7am–10pm' },
  { id: '6', name: 'Nakuru Auto Garage', category: 'Automotive', location: 'Nakuru Town', rating: 4.3, reviews: 178, phone: '+254 722 678 901', verified: true, description: 'Full car service, diagnostics, and repairs. All makes and models.', hours: 'Mon–Sat 7am–5pm' },
];

const CATEGORIES = ['All', 'Hardware', 'Fashion', 'Restaurant', 'Electronics', 'Supermarket', 'Automotive'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];

export default function BusinessDirectoryPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [location, setLocation] = useState('All Locations');

  const filtered = BUSINESSES.filter(b => {
    const matchSearch = !search || b.name.toLowerCase().includes(search.toLowerCase()) || b.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || b.category === category;
    const matchLoc = location === 'All Locations' || b.location.includes(location);
    return matchSearch && matchCat && matchLoc;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00BCD4 0%, transparent 50%), radial-gradient(circle at 70% 30%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.3)' }}>
              <Building2 size={12} /> Business Directory Kenya
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find Local <span style={{ color: '#00BCD4' }}>Businesses</span> Near You
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Discover verified businesses across Kenya. Restaurants, shops, services, and more.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search businesses…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(0,188,212,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#00BCD4' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(0,188,212,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {LOCATIONS.map(l => (
              <button key={l} onClick={() => setLocation(l)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: location === l ? 'rgba(124,77,255,0.15)' : 'rgba(255,255,255,0.04)', color: location === l ? '#7C4DFF' : 'rgba(255,255,255,0.55)', border: `1px solid ${location === l ? 'rgba(124,77,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00BCD4', fontWeight: 700 }}>{filtered.length}</span> businesses found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00BCD4', color: '#000' }}>
              List Business <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            {filtered.map(b => (
              <div key={b.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(0,188,212,0.12)', border: '1px solid rgba(0,188,212,0.2)' }}>
                    <Building2 size={20} style={{ color: '#00BCD4' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-700 text-white">{b.name}</h3>
                          {b.verified && <Shield size={12} style={{ color: '#00C853' }} />}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{b.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Star size={11} fill="#FFB300" style={{ color: '#FFB300' }} />
                        <span className="text-xs font-700" style={{ color: '#FFB300' }}>{b.rating}</span>
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>({b.reviews})</span>
                      </div>
                    </div>
                    <p className="text-xs mt-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{b.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-1">
                        <Phone size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{b.phone}</span>
                      </div>
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.25)' }}>
                        View <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(124,77,255,0.1))', border: '1px solid rgba(0,188,212,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">List Your Business</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Get discovered by thousands of customers searching for your services.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#00BCD4', color: '#000' }}>
              <Building2 size={16} /> Add Your Business
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
